// New account — one form, used by Users and by Crew's "Add crew member".
//
//   POST /api/admin/users   { name, email, role, timezone?, password? }   admin only
//
// The password may be left blank (owner, 2026-09-26: "allow leaving password
// empty and sending a temp default password on email for user to change
// it"). The server then makes the account its OWN temporary password and
// emails it (server/utils/welcomeAccount.js), and says what happened in
// `welcome`. WelcomeNotice below says it to the admin — and when the email did
// not go, shows the password once, because otherwise nobody knows it.
import React, { useState } from 'react';
import { post } from '../../shared/http';
import { ROLE_OPTIONS } from '../../shared/roles';
import { FwButton, FwField, FwInput, FwSelect, Sheet, Notice } from '../../../ui/fw';
import { useAction, FormError, FormRow, SheetFooter } from '../records/OfficePage';
import './admin.css';

/** The sentence and the password to show after an account was made. Pure, so it is testable. */
export function welcomeMessage(created) {
  if (!created) return null;
  const who = created.name || created.email;
  const w = created.welcome;
  if (!w) return { tone: 'ok', text: `Account created for ${who}. Give them the password you chose.` };
  if (w.emailed) {
    // A wrong address still "sends" and bounces later, so say the way out.
    return { tone: 'ok', text: `Account created for ${who}. A temporary password was emailed to ${w.to}; they change it under Me → Change password once signed in. If it doesn't arrive, reset their password from Admin → Users.` };
  }
  return {
    tone: 'warn',
    text: `Account created for ${who}, but the email did not go: ${w.reason} Give them this temporary password — it will not be shown again.`,
    password: w.temp_password,
  };
}

// The sentence, the password and Done each on a line of their own. Inline,
// JSX put nothing between them and the admin read "…it will not be shown
// again.abcd-EFGH-3467Done" — on the one screen this password ever appears on,
// to be read off a phone and typed into another (review on #713). admin.css
// .ad-welcome__* sets them apart.
export function WelcomeNotice({ created, onDismiss }) {
  const m = welcomeMessage(created);
  if (!m) return null;
  return (
    <Notice tone={m.tone} data-welcome={m.password ? 'not-emailed' : 'ok'}>
      <p className="ad-welcome__text">{m.text}</p>
      {m.password ? <code className="ad-welcome__password" data-temp-password>{m.password}</code> : null}
      <div className="ad-welcome__actions">
        <FwButton variant="ghost" onClick={onDismiss} data-action="dismiss-welcome">Done</FwButton>
      </div>
    </Notice>
  );
}

export default function NewAccountSheet({ open, onClose, onCreated, defaultRole = 'worker', title = 'New account' }) {
  const blank = { name: '', email: '', password: '', role: defaultRole, timezone: '' };
  const [form, setForm] = useState(blank);
  const { busy, error, run, clear } = useAction();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // The sheet stays mounted between uses, so closing it — Cancel or the
  // sheet's own close — must forget what was typed. A blank password MEANS
  // "email them their own", so one left over from the last person would be
  // silently used for the next (review on #713).
  const close = () => { setForm(blank); clear(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    const body = { name: form.name.trim(), email: form.email.trim(), role: form.role };
    // Blank means "email them one" — so it is left OUT, not sent as ''.
    if (form.password) body.password = form.password;
    if (form.timezone.trim()) body.timezone = form.timezone.trim();
    let created = null;
    const ok = await run(async () => { created = await post('/admin/users', body); }, 'Could not create this account.');
    if (ok) { setForm(blank); onCreated(created); }
  };

  return (
    <Sheet open={open} title={title} onClose={close} footer={<SheetFooter onCancel={close} busy={busy} submitLabel="Create account" formId="new-user" />}>
      <form id="new-user" className="or-form" onSubmit={submit}>
        <FormError error={error} />
        <FormRow>
          <FwField label="Name" htmlFor="nu-name" required><FwInput id="nu-name" value={form.name} onChange={set('name')} required /></FwField>
          <FwField label="Email" htmlFor="nu-email" required><FwInput id="nu-email" type="email" value={form.email} onChange={set('email')} required /></FwField>
        </FormRow>
        <FormRow>
          <FwField label="Role" htmlFor="nu-role">
            <FwSelect id="nu-role" value={form.role} onChange={set('role')}>
              {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </FwSelect>
          </FwField>
          <FwField label="Timezone" htmlFor="nu-tz" hint="Blank uses the company timezone."><FwInput id="nu-tz" value={form.timezone} onChange={set('timezone')} placeholder="America/New_York" /></FwField>
        </FormRow>
        <FwField label="First password" htmlFor="nu-pw" hint="Leave blank and they are emailed their own temporary password to change once signed in. Or set one of at least 8 characters.">
          <FwInput id="nu-pw" type="password" autoComplete="new-password" minLength={8} value={form.password} onChange={set('password')} />
        </FwField>
      </form>
    </Sheet>
  );
}
